import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import {
  ACCOUNTING_METHOD_TYPES,
  ACCOUNTING_METHODS,
} from "constants/accountMethod";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import { LT_SERIAL_NUMBER_PREFIX } from "constants/journalEntrySources";
import dayjs from "dayjs";
import { Form, Formik, FormikHelpers } from "formik";
import { journalEntrySchema } from "formValidations/JournalEntrySchema";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import randomBytes from "randombytes";
import { useMemo } from "react";
import { useAddLedgerEntryMutation } from "store/apis/generalLedger";
import { FileData } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import {
  getMaxExistingSerialNumber,
  maybeCopyAccrualToCash,
} from "utils/journalEntryUtils";
import { getRecurringEntryStartDate } from "util/getRecurringEntryStartDate";
import { AddEditEntries } from "./AddEditEntries";
import { JournalEntryFormWrapper } from "./JournalEntryFormWrapper";
import { JournalEntryInfo } from "./JournalEntryInfo";

export type JournalEntryTransaction = {
  id: string;
  merchant?: string | null;
  cash_category_id?: string | null;
  accrual_category_id?: string | null;
  description?: string | null;
  credit: number;
  debit: number;
  invoice: Omit<FileData, "invoice_status" | "invoice_uuid"> | null;
  uuid: string;
  // this is only for FE to show txns in respective sections
  entry_type: ACCOUNTING_METHOD_TYPES;
  ledger_entry_serial_number?: string;
  classification_ids?: string[];
};

export const initialTransaction: Omit<JournalEntryTransaction, "id"> = {
  merchant: "",
  cash_category_id: "",
  accrual_category_id: "",
  description: "",
  credit: 0,
  debit: 0,
  invoice: null,
  uuid: "",
  entry_type: ACCOUNTING_METHODS.ACCRUAL,
  classification_ids: [],
};

export const AddJournalEntryModal = ({ close, isOpen }: ModalProps) => {
  const { alertToast, successToast } = useToast();
  const [addLedgerEntry, { isLoading: addingLedgerEntry }] =
    useAddLedgerEntryMutation();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();

  const initialTransactions = useMemo(
    () =>
      Array.from({ length: 2 }).map((arr, i) => ({
        id: `${randomBytes(10).toString("hex")}NEW_ENTRY`,
        ledger_entry_serial_number: `${LT_SERIAL_NUMBER_PREFIX}${(
          i + 101
        ).toString()}`,
        ...initialTransaction,
      })),
    []
  );

  const initialValues = {
    transactions: initialTransactions,
    transaction_date: dayjs().format(DISPLAY_DATE),
    ledger_entry_name: "",
    ledger_type: "",
    other_ledger_type_str: "",
    is_recurring_enabled: false,
    frequency: "MONTHLY",
    end_date: "",
    max_recurring_count: 0,
    ends_on: "NEVER_ENDS",
    is_cash_same_as_accrual: false,
    start_on: getRecurringEntryStartDate(dayjs().format(DISPLAY_DATE)),
    basis: ACCOUNTING_METHODS.ACCRUAL,
    entry_level_invoice: null as Omit<
      FileData,
      "invoice_status" | "invoice_uuid"
    > | null,
  };

  const onSubmit = async (
    values: typeof initialValues,
    { resetForm }: FormikHelpers<typeof initialValues>
  ) => {
    const {
      transactions,
      transaction_date,
      ledger_entry_name,
      ledger_type,
      other_ledger_type_str,
      is_cash_same_as_accrual,
      basis,
      entry_level_invoice,
    } = values;
    const transactionList = maybeCopyAccrualToCash(
      transactions,
      Boolean(is_cash_same_as_accrual)
    ).map(
      ({
        credit,
        debit,
        cash_category_id,
        accrual_category_id,
        description,
        invoice,
        merchant,
        classification_ids,
      }) => ({
        amount: Math.abs(Number(credit)) || Math.abs(Number(debit)) * -1,
        description: description!,
        invoice_id: entry_level_invoice?.uuid || invoice?.uuid || null,
        merchant_data_id: merchant,
        cash_category_id: cash_category_id || null,
        accrual_category_id: accrual_category_id || null,
        classification_ids: classification_ids || [],
      })
    );

    try {
      await addLedgerEntry({
        entityId,
        groupId,
        transactions: transactionList,
        transaction_date: dayjs(transaction_date).format(YYYY_MM_DD),
        ledger_entry_name,
        ledger_type: ledger_type || undefined,
        other_ledger_type_str: other_ledger_type_str || undefined,
        is_recurring_enabled: values.is_recurring_enabled,
        is_cash_same_as_accrual: Boolean(is_cash_same_as_accrual),
        max_recurring_count: values.end_date
          ? undefined
          : Number(values.max_recurring_count),
        frequency: values.frequency,
        end_date:
          values.end_date && !values.max_recurring_count
            ? dayjs(values.end_date).format(YYYY_MM_DD)
            : undefined,
        basis,
      }).unwrap();

      successToast({ message: "Journal Entry has been populated!" });
      resetForm();
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnChange
      validationSchema={journalEntrySchema}
    >
      {({ submitForm, isSubmitting, values, dirty }) => (
        <Modal.Root
          open={isOpen}
          onOpenChange={() => close({ takeConfirmation: dirty })}
        >
          <Form className="t-m-0 t-w-full">
            <JournalEntryFormWrapper>
              {({
                isValidTransactions,
                isAmountMatch,
                cashAndAccrualMatch,
              }) => (
                <Modal.Content size="xxxl" useCustomOverlay>
                  <Modal.Header>
                    <div className="t-w-full">
                      <Modal.Title>Add Journal Entry</Modal.Title>
                      <Modal.Subtitle>
                        Please make sure the total debit amount matches the
                        total credit amount
                      </Modal.Subtitle>
                    </div>
                    <Modal.Close type="button" />
                  </Modal.Header>
                  <Modal.Body className="t-gap-4  t-flex t-flex-col">
                    <div className="t-flex t-flex-col t-gap-4">
                      <JournalEntryInfo />
                      <AddEditEntries
                        cashAndAccrualMatch={cashAndAccrualMatch}
                        maxSerialNumber={getMaxExistingSerialNumber([])}
                      />
                    </div>
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Button onClick={() => close()} type="reset">
                      Cancel
                    </Button>

                    <ConditionalToolTip
                      condition={
                        !isAmountMatch && (
                          <span>
                            Total debits and credits
                            <br /> should match
                          </span>
                        )
                      }
                    >
                      <span>
                        <Button
                          customType="primary"
                          type="submit"
                          onClick={submitForm}
                          disabled={!isValidTransactions || addingLedgerEntry}
                          isLoading={addingLedgerEntry}
                        >
                          Add
                        </Button>
                      </span>
                    </ConditionalToolTip>
                  </Modal.FooterButtonGroup>
                </Modal.Content>
              )}
            </JournalEntryFormWrapper>
          </Form>
        </Modal.Root>
      )}
    </Formik>
  );
};
